import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../../context/LanguageContext';
import Footer from '../Footer';
import './ServicePage.css';

const DimosiaDioikisi = () => {
  const navigate = useNavigate();
  const { t, tArray, language } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fallback to Greek if translation not available
  const serviceKey = 'servicePage.dimosiaDioikisi';
  const hasTranslation = language !== 'el';
  
  const features = hasTranslation ? tArray(`${serviceKey}.features`) : [];
  const title = hasTranslation ? t(`${serviceKey}.title`) : 'AI for Public Administration';
  const subtitle = hasTranslation ? t(`${serviceKey}.subtitle`) : 'Transform your public sector organization with advanced AI solutions';
  const description = hasTranslation ? t(`${serviceKey}.description`) : '';

  const useCasesEl = [
    {
      title: "Whisper Police — Speech-to-Text for Law Enforcement",
      description: "Deployed with the Hellenic Police. 98.9% transcription accuracy across 100+ languages for interview documentation and evidence processing."
    },
    {
      title: "Railway Safety Transcription System",
      description: "Real-time transcription system for the Ministry of Transport. Converts voice communications into searchable text for safety analysis and incident investigation."
    },
    {
      title: "NOMOKRATIA.AI — Legal Document Intelligence",
      description: "Processes 3.5M+ EU legal documents from EUR-Lex, Council of State, Supreme Court, and Diavgeia. RAG-based semantic search across Greek and European law."
    },
    {
      title: "AI-Powered Blood Analysis",
      description: "Production system deployed with the Ministry of Health for automated blood sample analysis and diagnostic support."
    },
    {
      title: "DSA & AI Act Chatbot for European Commission",
      description: "Currently building multilingual chatbot for DG CNECT to help citizens and businesses understand Digital Services Act and AI Act requirements."
    }
  ];

  const faqsEl = [
    {
      question: "What is AI for Public Administration?",
      answer: "AI for Public Administration includes advanced systems that automate processes, analyze data, and improve citizen services while ensuring GDPR compliance and EU regulatory alignment."
    },
    {
      question: "How does AI help Public Sector organizations?",
      answer: "AI reduces document processing time by 70%, provides 24/7 citizen service chatbots, enables data-driven policy decisions, and improves transparency through systems like NOMOKRATIA.AI."
    },
    {
      question: "Are PROTOS AI systems compliant with EU regulations?",
      answer: "Yes, all our systems are built for EU AI Act compliance, full GDPR conformity, and aligned with NIS2 and DSA requirements. We are EU AI Pact Signatories and Registered EU Experts (EX2025D1322797)."
    }
  ];

  // Use features from translation or fallback
  const displayFeatures = features.length > 0 ? features : useCasesEl.map(uc => ({
    icon: '⚡',
    title: uc.title,
    description: uc.description
  }));

  const displayFaqs = faqsEl;

  return (
    <>
      <Helmet>
        <title>AI for Public Administration — PROTOS AI Agency | Government AI Solutions</title>
        <meta name="description" content="Production-grade AI for government agencies. Speech-to-text (98.9% accuracy), document automation, citizen chatbots. Deployed with Hellenic Police & Ministry of Transport." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://ai-agency.gr/services/dimosia-dioikisi" />
        
        {/* Hreflang Tags */}
        <link rel="alternate" hreflang="en" href="https://ai-agency.gr/en/services/dimosia-dioikisi" />
        <link rel="alternate" hreflang="de" href="https://ai-agency.gr/de/services/dimosia-dioikisi" />
        <link rel="alternate" hreflang="el" href="https://ai-agency.gr/el/services/dimosia-dioikisi" />
        <link rel="alternate" hreflang="x-default" href="https://ai-agency.gr/en/services/dimosia-dioikisi" />
        
        {/* Open Graph */}
        <meta property="og:title" content="AI for Public Administration — Government AI Solutions" />
        <meta property="og:description" content="Production-grade AI for government agencies. Deployed with Hellenic Police, Ministry of Transport, Ministry of Health." />
        <meta property="og:url" content="https://ai-agency.gr/services/dimosia-dioikisi" />
        <meta property="og:type" content="website" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="AI for Public Administration" />
        <meta name="twitter:description" content="Production-grade AI for government. 98.9% accuracy speech-to-text, NOMOKRATIA.AI legal intelligence." />
        
        {/* Enhanced Organization Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "PROTOS AI Agency",
            "alternateName": "PROTOS AI Agency IKE",
            "url": "https://ai-agency.gr",
            "description": "European Regulatory AI company. AI Act, DSA, NIS2, GDPR compliance.",
            "foundingDate": "2021",
            "founder": {
              "@type": "Person",
              "name": "Grigorios Tsinaforniotis",
              "jobTitle": "CEO"
            },
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Archaiologikou Mouseiou 17",
              "addressLocality": "Thessaloniki",
              "postalCode": "54640",
              "addressCountry": "GR"
            },
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+30-698-889-9000",
              "email": "info@ai-agency.gr",
              "contactType": "sales"
            },
            "sameAs": [
              "https://www.wikidata.org/wiki/Q137993719",
              "https://www.linkedin.com/company/protos-ai-agency/"
            ],
            "award": [
              "EU AI Pact Signatory",
              "Registered EU Expert (EX2025D1322797)",
              "EU AI Alliance Member"
            ]
          })}
        </script>
        
        {/* Service Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "AI for Public Administration",
            "provider": {
              "@type": "Organization",
              "name": "PROTOS AI Agency"
            },
            "areaServed": "European Union",
            "description": "Production-grade AI systems for government agencies including speech-to-text, document intelligence, and citizen service automation."
          })}
        </script>

        {/* FAQ Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": displayFaqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
              }
            }))
          })}
        </script>

        {/* Schema.org BreadcrumbList */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Αρχική",
                "item": "https://ai-agency.gr/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Υπηρεσίες",
                "item": "https://ai-agency.gr/#services"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": "AI για Δημόσια Διοίκηση",
                "item": "https://ai-agency.gr/services/dimosia-dioikisi"
              }
            ]
          })}
        </script>
      </Helmet>

      <div className="service-page">
        {/* Header */}
        <header className="service-header">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <button 
              onClick={() => navigate('/')}
              className="text-blue-400 hover:text-blue-300 transition-colors flex items-center space-x-2 mb-4"
            >
              <span>←</span>
              <span>{t('nav.backToHome')}</span>
            </button>
            
            {/* Breadcrumb Navigation */}
            <nav aria-label="breadcrumb" className="text-sm text-gray-400 mb-2">
              <ol className="flex items-center space-x-2">
                <li>
                  <a href="/" className="hover:text-blue-400 transition-colors">{t('servicePage.breadcrumb.home')}</a>
                </li>
                <li><span>/</span></li>
                <li>
                  <a href="/#services" className="hover:text-blue-400 transition-colors">{t('servicePage.breadcrumb.services')}</a>
                </li>
                <li><span>/</span></li>
                <li className="text-gray-300" aria-current="page">{t('services.dimosiaDioikisi')}</li>
              </ol>
            </nav>
          </div>
        </header>

        {/* Hero Section */}
        <section className="service-hero">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              {title}
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              {subtitle}
            </p>
          </div>
        </section>

        {/* Main Content */}
        <section className="service-content">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="prose prose-lg prose-invert max-w-none">
              {description ? (
                <p className="text-lg text-gray-300 leading-relaxed mb-6">{description}</p>
              ) : (
                <>
                  <p className="text-lg text-gray-300 leading-relaxed mb-6">
                    The digital transformation of public sector organizations is a priority for improving efficiency, transparency, and citizen services. Artificial Intelligence (AI) offers innovative solutions that transform the way public services operate.
                  </p>
                  <p className="text-lg text-gray-300 leading-relaxed mb-6">
                    Our solutions for public administration are designed specifically for government ministries, regions, municipalities, and other public entities. We ensure the highest standards of security and full GDPR compliance, aligned with EU AI Act and NIS2 requirements.
                  </p>
                </>
              )}
            </div>

            {/* Features Grid */}
            {displayFeatures.length > 0 && (
              <div className="mt-12">
                <h2 className="text-3xl font-bold text-white mb-8 text-center">
                  {hasTranslation ? t(`${serviceKey}.featuresTitle`) : 'Core Capabilities'}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {displayFeatures.map((feature, index) => (
                    <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:border-blue-400/50 transition-all">
                      <div className="text-3xl mb-4">{feature.icon}</div>
                      <h3 className="text-xl font-bold text-blue-400 mb-3">{feature.title}</h3>
                      <p className="text-gray-300">{feature.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Use Cases */}
        <section className="use-cases-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <h2 className="text-4xl font-bold text-center mb-4 text-white">
              {hasTranslation ? t('servicePage.useCasesTitle') : 'Production Deployments'}
            </h2>
            <p className="text-lg text-gray-400 text-center mb-12">
              {hasTranslation ? t('servicePage.useCasesSubtitle') : 'Real AI systems running in real government agencies'}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {useCasesEl.map((useCase, index) => (
                <div key={index} className="use-case-card">
                  <h3 className="text-xl font-bold mb-3 text-blue-400">{useCase.title}</h3>
                  <p className="text-gray-300">{useCase.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-black/30 relative z-10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-bold text-center mb-12 text-white">
              {hasTranslation ? t('servicePage.faqTitle') : 'Frequently Asked Questions'}
            </h2>
            <div className="space-y-6">
              {displayFaqs.map((faq, index) => (
                <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-3 text-blue-400">{faq.question}</h3>
                  <p className="text-gray-300 leading-relaxed">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="cta-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
            <h2 className="text-4xl font-bold mb-6 text-white">
              Ξεκινήστε τον Ψηφιακό Μετασχηματισμό
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Επικοινωνήστε μαζί μας για μια δωρεάν συμβουλευτική συνεδρία και ανακαλύψτε πώς η Τεχνητή Νοημοσύνη μπορεί να μετασχηματίσει τον δημόσιο φορέα σας.
            </p>
            <button 
              onClick={() => {
                navigate('/');
                setTimeout(() => {
                  const contactSection = document.getElementById('contact');
                  if (contactSection) {
                    contactSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }, 100);
              }}
              className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:from-blue-600 hover:to-cyan-600 transition-all transform hover:scale-105"
            >
              {t('servicePage.ctaButton')}
            </button>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default DimosiaDioikisi;